import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { Gift, Search, Loader2, Package, DollarSign, X, Settings, Sparkles } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Card } from '../ui/card';
import { Product } from '../../types';
import { fetchAllProducts } from '../../services/productService';
import { createFreeItemReward } from '../../services/rewardsService';

interface CreateFreeItemRewardModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRewardCreated: () => void;
}

export function CreateFreeItemRewardModal({
  isOpen,
  onClose,
  onRewardCreated,
}: CreateFreeItemRewardModalProps) {
  const [threshold, setThreshold] = useState<string>('');
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [milestoneLabel, setMilestoneLabel] = useState<string>('');
  const [products, setProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [showProductPicker, setShowProductPicker] = useState(false);

  // Fetch products on mount - only $0 products for free rewards
  useEffect(() => {
    const loadProducts = async () => {
      try {
        setLoadingProducts(true);
        const productsData = await fetchAllProducts();
        const freeProducts = productsData.filter(p => !p.disabled && p.price === 0);
        setProducts(freeProducts);
        setFilteredProducts(freeProducts);
      } catch (error) {
        console.error('Error loading products:', error);
        toast.error('Failed to load products');
      } finally {
        setLoadingProducts(false);
      }
    };

    if (isOpen) {
      loadProducts();
    }
  }, [isOpen]);

  // Filter products based on search
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredProducts(products);
    } else {
      const filtered = products.filter(product =>
        product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (product.brand && product.brand.toLowerCase().includes(searchQuery.toLowerCase()))
      );
      setFilteredProducts(filtered);
    }
  }, [searchQuery, products]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!threshold) {
      toast.error('Please enter a threshold amount');
      return;
    }

    const thresholdValue = parseFloat(threshold);
    if (isNaN(thresholdValue) || thresholdValue <= 0) {
      toast.error('Please enter a valid threshold amount');
      return;
    }

    setLoading(true);

    try {
      await createFreeItemReward(
        thresholdValue,
        selectedProduct?.id || null,
        selectedProduct ? null : (milestoneLabel.trim() || null)
      );

      const message = selectedProduct 
        ? `Free item reward created successfully with ${selectedProduct.name}`
        : milestoneLabel.trim() 
          ? `Milestone "${milestoneLabel.trim()}" created successfully`
          : 'Threshold milestone created successfully';
      
      toast.success(message);
      onRewardCreated();
      handleClose();
    } catch (error) {
      console.error('Error creating reward:', error);
      toast.error('Failed to create reward');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) return;
    onClose();
    // Reset form after animation completes
    setTimeout(() => {
      setThreshold('');
      setSelectedProduct(null);
      setMilestoneLabel('');
      setSearchQuery('');
      setShowProductPicker(false);
    }, 300);
  };

  const selectProduct = (product: Product) => {
    setSelectedProduct(product);
    setShowProductPicker(false);
    setSearchQuery('');
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[10001] flex items-center justify-center p-4"
          style={{ margin: 0, padding: '1rem', top: 0, left: 0, right: 0, bottom: 0 }}
          onClick={(e) => e.target === e.currentTarget && handleClose()}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: "spring", duration: 0.3 }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Enhanced Header */}
            <div className="relative bg-gradient-to-r from-jamaica-green to-jamaica-green/80 px-8 py-6">
              <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iMiIvPjwvZz48L2c+PC9zdmc=')] opacity-20"></div>
              
              <div className="relative flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <motion.div 
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: "spring", bounce: 0.6 }}
                    className="h-12 w-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/30"
                  >
                    <Gift className="h-6 w-6 text-white" />
                  </motion.div>
                  <div>
                    <motion.h2 
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 }}
                      className="text-2xl font-bold text-white"
                    >
                      Create Free Item Reward
                    </motion.h2>
                    <motion.p 
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.2 }}
                      className="text-white/80 text-sm"
                    >
                      Set up a new order value threshold reward
                    </motion.p>
                  </div>
                </div>
                <motion.div
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={handleClose}
                    disabled={loading}
                    className="text-white hover:bg-white/20 h-10 w-10 rounded-full"
                  >
                    <X className="h-5 w-5" />
                  </Button>
                </motion.div>
              </div>
            </div>

            {/* Enhanced Content */}
            <form onSubmit={handleSubmit} className="p-8 space-y-8 max-h-[calc(90vh-200px)] overflow-y-auto">
              {/* Basic Information */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="space-y-6"
              >
                <div className="flex items-center gap-3 pb-3 border-b border-gray-100">
                  <div className="h-8 w-8 bg-gradient-to-r from-jamaica-green to-jamaica-green/80 rounded-lg flex items-center justify-center">
                    <Settings className="h-4 w-4 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Reward Configuration</h3>
                </div>

                <div className="space-y-6">
                  {/* Threshold Input */}
                  <div className="space-y-3">
                    <Label htmlFor="threshold" className="text-base font-semibold text-gray-900">
                      Order Value Threshold *
                    </Label>
                    <div className="relative">
                      <DollarSign className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <Input
                        id="threshold"
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        value={threshold}
                        onChange={(e) => setThreshold(e.target.value)}
                        className="h-12 pl-12 rounded-xl border-gray-200 text-base"
                        required
                      />
                    </div>
                    <p className="text-sm text-gray-500">
                      Customers will earn this free item when their cart total reaches this amount
                    </p>
                  </div>

                  {/* Product Selection */}
                  <div className="space-y-3">
                    <Label className="text-base font-semibold text-gray-900">
                      Free Product <span className="text-sm font-normal text-gray-500">(Optional - Free items only $0.00)</span>
                    </Label>
                    
                    {!showProductPicker ? (
                      <div className="space-y-3">
                        {selectedProduct ? (
                          <Card className="p-4 border border-gray-200 rounded-xl">
                            <div className="flex items-center gap-3">
                              <img
                                src={selectedProduct.image}
                                alt={selectedProduct.name}
                                className="w-12 h-12 rounded-xl object-cover"
                              />
                              <div className="flex-1">
                                <h4 className="font-semibold text-base">{selectedProduct.name}</h4>
                                <div className="flex items-center gap-2 mt-1">
                                  <Badge className="bg-green-100 text-green-800 text-xs">
                                    FREE
                                  </Badge>
                                  <Badge variant="outline" className="text-xs">
                                    {selectedProduct.category}
                                  </Badge>
                                  {selectedProduct.isBestSeller && (
                                    <Badge className="bg-jamaica-gold text-white text-xs">
                                      Best Seller
                                    </Badge>
                                  )}
                                </div>
                              </div>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => setShowProductPicker(true)}
                                className="rounded-lg"
                              >
                                Change
                              </Button>
                            </div>
                          </Card>
                        ) : (
                          <div className="space-y-3">
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => setShowProductPicker(true)}
                              className="w-full h-20 border-dashed border-2 flex flex-col items-center gap-2 hover:border-jamaica-green hover:text-jamaica-green rounded-xl"
                            >
                              <Package className="h-6 w-6" />
                              <span>Select Free Product (Optional)</span>
                            </Button>
                            <p className="text-sm text-gray-500 text-center">
                              Leave empty to create a milestone without auto-adding a product
                            </p>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-4 border border-gray-200 rounded-xl p-6">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Sparkles className="h-5 w-5 text-jamaica-green" />
                            <h4 className="font-semibold text-base">Select Free Product</h4>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setShowProductPicker(false)}
                            className="rounded-lg"
                          >
                            Cancel
                          </Button>
                        </div>
                        
                        {/* Search */}
                        <div className="relative">
                          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                          <Input
                            placeholder="Search free products..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="h-12 pl-12 rounded-xl border-gray-200"
                          />
                        </div>

                        {/* Product List */}
                        <div className="max-h-64 overflow-y-auto space-y-3">
                          {loadingProducts ? (
                            <div className="flex items-center justify-center py-12">
                              <Loader2 className="h-6 w-6 animate-spin text-jamaica-green" />
                              <span className="ml-2 text-base text-gray-600">Loading free products...</span>
                            </div>
                          ) : filteredProducts.length === 0 ? (
                            <div className="text-center py-12">
                              <Package className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                              <p className="text-base text-gray-500 mb-2">No free products found</p>
                              <p className="text-sm text-gray-400">Only products priced at $0.00 can be used as free rewards</p>
                            </div>
                          ) : (
                            filteredProducts.map((product) => (
                              <motion.div
                                key={product.id}
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                              >
                                <Card 
                                  className="p-4 cursor-pointer hover:border-jamaica-green transition-all duration-200 rounded-xl"
                                  onClick={() => selectProduct(product)}
                                >
                                  <div className="flex items-center gap-4">
                                    <img
                                      src={product.image}
                                      alt={product.name}
                                      className="w-12 h-12 rounded-xl object-cover"
                                    />
                                    <div className="flex-1 min-w-0">
                                      <h5 className="font-semibold text-base truncate">{product.name}</h5>
                                      <div className="flex items-center gap-2 mt-1">
                                        <Badge className="bg-green-100 text-green-800 text-xs">
                                          FREE
                                        </Badge>
                                        <Badge variant="outline" className="text-xs">
                                          {product.category}
                                        </Badge>
                                        {product.isBestSeller && (
                                          <Badge className="bg-jamaica-gold text-white text-xs">
                                            Best Seller
                                          </Badge>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </Card>
                              </motion.div>
                            ))
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Milestone Label (only show when no product selected) */}
                  {!selectedProduct && (
                    <div className="space-y-3">
                      <Label className="text-base font-semibold text-gray-900">
                        Milestone Label <span className="text-sm font-normal text-gray-500">(Optional)</span>
                      </Label>
                      <Input
                        type="text"
                        placeholder="e.g., '10% Off Next Order', 'VIP Status Unlocked'"
                        value={milestoneLabel}
                        onChange={(e) => setMilestoneLabel(e.target.value)}
                        className="h-12 rounded-xl border-gray-200 text-base"
                        maxLength={50}
                      />
                      <p className="text-sm text-gray-500">
                        This label will be displayed to customers when they reach this milestone
                      </p>
                    </div>
                  )}
                </div>
              </motion.div>

              {/* Enhanced Action Buttons */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="flex gap-4 pt-6 border-t border-gray-100"
              >
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  disabled={loading}
                  className="flex-1 h-12 rounded-xl border-gray-200 hover:bg-gray-50"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading || !threshold}
                  className="flex-1 h-12 bg-gradient-to-r from-jamaica-green to-jamaica-green/90 hover:from-jamaica-green/90 hover:to-jamaica-green text-white rounded-xl font-semibold"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Creating Reward...
                    </>
                  ) : (
                    <>
                      <Gift className="w-5 h-5 mr-2" />
                      Create Reward
                    </>
                  )}
                </Button>
              </motion.div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}